import { useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export const useGSAPAnimations = (mainRef) => {
  useEffect(() => {
    if (!mainRef.current) return;

    const ctx = gsap.context(() => {

      // ╔══════════════════════════════════════════╗
      // ║  HERO: AUTO-INTRO ON PAGE LOAD (FAST)    ║
      // ╚══════════════════════════════════════════╝

      gsap.set("#hero-badge", { opacity: 0, y: -20 });
      gsap.set("#hero-title", { opacity: 0, y: 40, clipPath: "inset(0 100% 0 0)" });
      gsap.set("#hero-sub", { opacity: 0, y: 20 });
      gsap.set(".profile-card", { opacity: 0, scale: 0.92, rotateX: 15, y: 40 });
      gsap.set("#hero-cta-row", { opacity: 0, y: 25 });

      const intro = gsap.timeline({ delay: 0.1 });
      intro
        .to("#hero-badge", { opacity: 1, y: 0, duration: 0.4, ease: "power3.out" })
        .to("#hero-title", {
          opacity: 1,
          y: 0,
          clipPath: "inset(0 0% 0 0)",
          duration: 0.6,
          ease: "expo.out"
        }, "-=0.25")
        .to("#hero-sub", { opacity: 1, y: 0, duration: 0.35, ease: "power2.out" }, "-=0.35")
        .to(".profile-card", {
          opacity: 1,
          scale: 1,
          rotateX: 0,
          y: 0,
          duration: 0.65,
          ease: "expo.out"
        }, "-=0.3")
        .to("#hero-cta-row", {
          opacity: 1,
          y: 0,
          duration: 0.4,
          ease: "back.out(1.6)"
        }, "-=0.35");

      // Shared snappy scrub settings
      const fastScrub = 0.5;

      // ╔══════════════════════════════════════════╗
      // ║  SOCIABUZZ: TRIBE BOOST REVEAL           ║
      // ╚══════════════════════════════════════════╝

      gsap.from("#sociabuzz-card", {
        y: 80,
        opacity: 0,
        scale: 0.92,
        rotateX: 20,
        transformOrigin: "center bottom",
        scrollTrigger: {
          trigger: "#sociabuzz",
          start: "top 85%",
          end: "top 55%",
          scrub: fastScrub
        }
      });

      // ╔══════════════════════════════════════════╗
      // ║  WHY CXM: WIPE + FLIP (SNAPPY)            ║
      // ╚══════════════════════════════════════════╝

      gsap.from("#why-title", {
        clipPath: "inset(0 100% 0 0)",
        y: 30,
        scrollTrigger: {
          trigger: "#why-cxm",
          start: "top 80%",
          end: "top 55%",
          scrub: fastScrub
        }
      });

      gsap.from("#why-badge", {
        y: -20,
        opacity: 0,
        scrollTrigger: {
          trigger: "#why-cxm",
          start: "top 85%",
          end: "top 65%",
          scrub: fastScrub
        }
      });

      gsap.from("#why-accent", {
        scaleX: 0,
        scrollTrigger: {
          trigger: "#why-accent",
          start: "top 85%",
          end: "top 65%",
          scrub: fastScrub
        }
      });

      gsap.utils.toArray(".benefit-card").forEach((card, i) => {
        gsap.from(card, {
          rotateY: 60,
          opacity: 0,
          y: 50,
          transformOrigin: "left center",
          scrollTrigger: {
            trigger: card,
            start: "top 92%",
            end: "top 65%",
            scrub: fastScrub + (i * 0.05)
          }
        });
      });

      // ╔══════════════════════════════════════════╗
      // ║  PROOF WALL: KEYNOTE PRESENTATION        ║
      // ╚══════════════════════════════════════════╝

      gsap.from("#proof-wall h2", {
        scale: 0.7,
        opacity: 0,
        clipPath: "inset(40% 0 40% 0)",
        scrollTrigger: {
          trigger: "#proof-wall",
          start: "top 80%",
          end: "top 55%",
          scrub: fastScrub
        }
      });

      gsap.utils.toArray(".certificate-card").forEach((card, i) => {
        gsap.from(card, {
          y: 70,
          opacity: 0,
          rotateX: 35,
          rotateY: i % 2 === 0 ? -10 : 10,
          scale: 0.9,
          transformOrigin: "center bottom",
          scrollTrigger: {
            trigger: card,
            start: "top 92%",
            end: "top 65%",
            scrub: fastScrub + (i * 0.05)
          }
        });
      });

      // ╔══════════════════════════════════════════╗
      // ║  HOW IT WORKS: STEPS (SNAPPY)            ║
      // ╚══════════════════════════════════════════╝

      gsap.from("#hiw-title", {
        clipPath: "inset(0 0 0 100%)",
        y: 30,
        scrollTrigger: {
          trigger: "#how-it-works",
          start: "top 80%",
          end: "top 55%",
          scrub: fastScrub
        }
      });

      gsap.from("#hiw-badge", {
        y: -20,
        opacity: 0,
        scrollTrigger: {
          trigger: "#how-it-works",
          start: "top 85%",
          end: "top 65%",
          scrub: fastScrub
        }
      });

      gsap.utils.toArray(".step-card").forEach((step, i) => {
        gsap.from(step, {
          y: 60,
          opacity: 0,
          rotateX: 45,
          scale: 0.88,
          transformOrigin: "center bottom",
          scrollTrigger: {
            trigger: step,
            start: "top 90%",
            end: "top 65%",
            scrub: fastScrub + (i * 0.1)
          }
        });
      });

      // ╔══════════════════════════════════════════╗
      // ║  CTA: BURST FINALE (SNAPPY)              ║
      // ╚══════════════════════════════════════════╝

      gsap.from("#cta h2", {
        clipPath: "inset(0 100% 0 0)",
        y: 40,
        scrollTrigger: {
          trigger: "#cta",
          start: "top 80%",
          end: "top 55%",
          scrub: fastScrub
        }
      });

      gsap.from(".cta-primary", {
        scale: 0.75,
        opacity: 0,
        rotateZ: -6,
        scrollTrigger: {
          trigger: "#cta",
          start: "top 75%",
          end: "top 55%",
          scrub: fastScrub
        }
      });

      gsap.from(".cta-secondary", {
        x: 60,
        opacity: 0,
        scrollTrigger: {
          trigger: "#cta",
          start: "top 75%",
          end: "top 55%",
          scrub: fastScrub
        }
      });

      // ╔══════════════════════════════════════════╗
      // ║  PARALLAX VIDEO BACKGROUND               ║
      // ╚══════════════════════════════════════════╝

      gsap.to("#bg-video", {
        scale: 1.15,
        y: "8%",
        scrollTrigger: {
          trigger: mainRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 1.5
        }
      });

      requestAnimationFrame(() => ScrollTrigger.refresh());

    }, mainRef);

    return () => ctx.revert();
  }, [mainRef]);
};
